import { Epoch, StudyScoringAggregationMethod } from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { ConfirmationModal } from '@components/shared/confirm-delete-modal.component';
import { useAppForm } from '@components/ui/form/form';
import { RadioGroup, RadioGroupItem } from '@components/ui/radio-group';
import { errorToast } from '@hooks/toast.hook';
import { cn } from '@lib/utils';
import {
  type MeiaRunEpochFormValues,
  type MeiaScoringConfigFormValues,
  meiaRunEpochSchema,
} from '@schemas/meia.schema';
import { createFormInvalidHandler } from '@utils/form.util';
import { type FC, useState } from 'react';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';
import { VerdictBandScale } from './meia-p4-dashboard-primitives.component';

export const EPOCH_OPTIONS: ReadonlyArray<{ label: string; value: Epoch }> = [
  { label: 'Near Term', value: Epoch.NEAR_TERM },
  { label: 'FYDP', value: Epoch.FYDP },
  { label: 'Beyond', value: Epoch.BEYOND },
];

export const DEFAULT_SCORING_CONFIG: MeiaScoringConfigFormValues = {
  aggregationMethod: StudyScoringAggregationMethod.WEAKEST_LINK,
  greenThreshold: 80,
  yellowThreshold: 60,
};

export const DEFAULT_RUN_EPOCH: Epoch = Epoch.NEAR_TERM;

export const SCORING_CONFIG_AGGREGATION_OPTIONS: ReadonlyArray<{
  description: string;
  label: string;
  value: MeiaScoringConfigFormValues['aggregationMethod'];
}> = [
  {
    description: 'Thread verdict is the lowest stage score.',
    label: 'Weakest Link',
    value: StudyScoringAggregationMethod.WEAKEST_LINK,
  },
  {
    description: 'Thread verdict is the weighted stage mean.',
    label: 'Weighted Average',
    value: StudyScoringAggregationMethod.WEIGHTED_AVERAGE,
  },
  {
    description: 'A failing stage blocks the thread rollup.',
    label: 'Gated Multiply',
    value: StudyScoringAggregationMethod.GATED_MULTIPLY,
  },
];

type MeiaScoringConfigModalProps = {
  config: MeiaRunEpochFormValues;
  isOpen: boolean;
  isRunning: boolean;
  onClose: () => void;
  onRunCase: (params: { config: MeiaRunEpochFormValues; isDirty: boolean }) => Promise<void>;
};

export const MeiaScoringConfigModal: FC<MeiaScoringConfigModalProps> = (props) => {
  const { config, isOpen, isRunning, onClose, onRunCase } = props;
  const [isDiscardChangesOpen, setIsDiscardChangesOpen] = useState(false);

  const form = useAppForm({
    defaultValues: config,
    onSubmit: async ({ value }) => {
      const validation = meiaRunEpochSchema.safeParse(value);
      if (!validation.success) {
        errorToast({ description: 'Please fix the scoring config values.' });
        return;
      }

      await onRunCase({ config: validation.data, isDirty: form.state.isDirty });
    },
    onSubmitInvalid: createFormInvalidHandler<typeof config>(),
    validators: { onSubmit: meiaRunEpochSchema },
  });

  const requestClose = (): void => {
    if (form.state.isDirty) {
      setIsDiscardChangesOpen(true);
      return;
    }
    onClose();
  };

  return (
    <>
      <Modal
        centered
        footer={{
          leftButton: {
            children: 'Cancel',
            disabled: isRunning,
            onClick: requestClose,
            type: 'button',
            variant: 'secondary',
          },
          rightButton: {
            children: isRunning ? 'Running...' : 'Run Case',
            disabled: isRunning,
            onClick: () => void form.handleSubmit(),
            type: 'button',
          },
        }}
        header={{ title: 'Scoring Config' }}
        isOpen={isOpen}
        onClose={requestClose}
        width="620px"
      >
        <form
          {...MEIA_AUTOFILL_IGNORE_PROPS}
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <form.AppField name="epoch">
              {(field) => (
                <section className="flex flex-col gap-2">
                  <div>
                    <div className="text-sm font-semibold text-gray-950">Epoch</div>
                    <div className="mt-1 text-xs text-gray-500">Which time horizon this run case targets.</div>
                  </div>
                  <RadioGroup
                    onValueChange={(value) => {
                      const option = EPOCH_OPTIONS.find((currentOption) => currentOption.value === value);
                      if (option) field.handleChange(option.value);
                    }}
                    value={field.state.value}
                  >
                    {EPOCH_OPTIONS.map((option) => (
                      <label
                        className={cn(
                          'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors',
                          field.state.value === option.value
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 bg-white hover:bg-gray-50',
                        )}
                        htmlFor={`scoring-config-epoch-${option.value}`}
                        key={option.value}
                      >
                        <RadioGroupItem
                          id={`scoring-config-epoch-${option.value}`}
                          value={option.value}
                        />
                        <span className="text-sm font-medium text-gray-950">{option.label}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </section>
              )}
            </form.AppField>

            <form.AppField name="aggregationMethod">
              {(field) => (
                <section className="flex flex-col gap-2">
                  <div>
                    <div className="text-sm font-semibold text-gray-950">Aggregation Method</div>
                    <div className="mt-1 text-xs text-gray-500">
                      How stage-level scores roll up into a thread verdict.
                    </div>
                  </div>
                  <RadioGroup
                    onValueChange={(value) => {
                      const option = SCORING_CONFIG_AGGREGATION_OPTIONS.find(
                        (currentOption) => currentOption.value === value,
                      );
                      if (option) field.handleChange(option.value);
                    }}
                    value={field.state.value}
                  >
                    {SCORING_CONFIG_AGGREGATION_OPTIONS.map((option) => (
                      <label
                        className={cn(
                          'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors',
                          field.state.value === option.value
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 bg-white hover:bg-gray-50',
                        )}
                        htmlFor={`scoring-config-${option.value}`}
                        key={option.value}
                      >
                        <RadioGroupItem
                          id={`scoring-config-${option.value}`}
                          value={option.value}
                        />
                        <span className="flex flex-col gap-1">
                          <span className="text-sm font-medium text-gray-950">{option.label}</span>
                          <span className="text-xs text-gray-500">{option.description}</span>
                        </span>
                      </label>
                    ))}
                  </RadioGroup>
                </section>
              )}
            </form.AppField>

            <section className="flex flex-col gap-3">
              <div>
                <div className="text-sm font-semibold text-gray-950">Verdict Bands</div>
                <div className="mt-1 text-xs text-gray-500">Thresholds for green / yellow / red verdicts.</div>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <form.Subscribe selector={(state) => state.values.yellowThreshold}>
                  {(yellowThreshold) => (
                    <div className="flex flex-col gap-1">
                      <label
                        className="font-mono text-[10px] uppercase tracking-wider text-gray-500"
                        htmlFor="scoring-config-red-threshold"
                      >
                        Red &lt;
                      </label>
                      <input
                        className="h-10 rounded-md border border-gray-200 bg-gray-100 px-3 text-sm text-gray-500"
                        disabled
                        id="scoring-config-red-threshold"
                        readOnly
                        value={yellowThreshold}
                      />
                    </div>
                  )}
                </form.Subscribe>
                <form.AppField name="yellowThreshold">
                  {(field) => (
                    <field.TextField
                      {...MEIA_AUTOFILL_IGNORE_PROPS}
                      label="Yellow >="
                      max={100}
                      min={0}
                      placeholder="60"
                      required
                      step={1}
                      type="number"
                    />
                  )}
                </form.AppField>
                <form.AppField name="greenThreshold">
                  {(field) => (
                    <field.TextField
                      {...MEIA_AUTOFILL_IGNORE_PROPS}
                      label="Green >="
                      max={100}
                      min={0}
                      placeholder="80"
                      required
                      step={1}
                      type="number"
                    />
                  )}
                </form.AppField>
              </div>
              <form.Subscribe
                selector={(state) => ({
                  greenThreshold: state.values.greenThreshold,
                  yellowThreshold: state.values.yellowThreshold,
                })}
              >
                {(thresholds) => <VerdictBandScale thresholds={thresholds} />}
              </form.Subscribe>
            </section>
          </form.AppForm>
        </form>
      </Modal>
      <ConfirmationModal
        cancelLabel="Keep Editing"
        confirmLabel="Discard Changes"
        description="Closing now will discard unsaved scoring config edits."
        isOpen={isDiscardChangesOpen}
        onConfirm={onClose}
        setIsOpen={setIsDiscardChangesOpen}
        title="Discard unsaved changes?"
      />
    </>
  );
};
